import React from "react"
import { useNavigate, useParams } from "react-router-dom"

import OrderHeader from "./OrderHeader"
import Mandate from "./mandate/Mandate"
import Report from "./report/Report"

import { OrderProvider, useOrder } from "../../contexts/OrderContext"
import { AudioProvider } from "@/contexts/AudioContext"
import { useAuth } from "@/contexts/AuthContext"
import Splash from "@/components/splash"
import Complete from "./Complete"
import { Card } from "@/components/ui/card"
import ReportPlayer from "./report/ReportPlayer"
import Documents from "./report/Documents"
import { Button } from "@/components/ui/button"
import { AlertCircleIcon, ArrowRightIcon, CheckCircle2Icon, CircleIcon, LoaderCircleIcon, SlashIcon } from "lucide-react"
import { InfoCircledIcon } from "@radix-ui/react-icons"
import Transcription from "./report/Transcription"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb"
import clsx from "clsx"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { DocumentsProvider } from "@/contexts/DocumentsContext"
import Submission from "./report/Submission"

import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@/components/ui/resizable"
import cournoyer from "@/cournoyer"
import Bot from "./Bot"


const { APP_API_URL } = import.meta.env

export default function Order() {
    const { id, page } = useParams()
    const { user } = useAuth()
    const { order } = useOrder()

    if (!user) {
        return <Splash>Chargement du compte...</Splash>
    }

    const leftPane = page === "report" ? <OrderPage /> : <Documents />
    const rightPane = page === "report" ? <Documents /> : <OrderPage />

    return (

        <DocumentsProvider>
            {!order.id && <Splash>Chargement de la commande</Splash>}
            {order.id && <div className="flex flex-col h-screen min-h-0">
                <OrderHeader order={order} />

                {<ResizablePanelGroup className={clsx("flex-grow flex gap-1 min-h-0 p-4 pt-2")} direction="horizontal">
                    <ResizablePanel className="flex flex-grow gap-2 flex-col min-h-0">
                        {order?.mandate?.path && <div className="flex items-center justify-between gap-6 pl-2">
                            {!order.isLayout &&
                                <ReportPlayer />
                            }

                            {order.isLayout && <div></div>}

                            <OrderPager />
                        </div>}

                        <ResizablePanelGroup className={clsx("flex flex-grow gap-1 min-h-0")} direction="horizontal">
                            <ResizablePanel className="flex flex-grow w-1/2 min-h-0">{leftPane}</ResizablePanel>
                            <ResizableHandle />
                            <ResizablePanel className="flex flex-grow w-1/2 min-h-0">{rightPane}</ResizablePanel>
                        </ResizablePanelGroup>

                    </ResizablePanel>
                </ResizablePanelGroup>}
            </div>}
        </DocumentsProvider>
    )
}

function OrderPage() {
    const { id, page } = useParams()
    const { order } = useOrder()

    if (!order?.mandate?.path) {
        return null
    }

    return <div className="min-h-0 flex flex-grow flex-col gap-2">
        {page === "mandate" && <Mandate />}
        {page === "report" && <Documents defaultDocument={"transcription"} />}
        {page === "validation" && <Submission />}
    </div>
}

export function OrderPager() {
    const { order } = useOrder()

    if (!order.id) {
        return
    }

    return <>
        <div className="flex items-center justify-center h-14">
            <Breadcrumb>
                <BreadcrumbList>
                    {order.isGenerated && <>
                        {<OrderPagerItem to="mandate" label="Mandat" isDone={order.mandate.reviewed} />}
                        {!order.isLayout && order.mandate.path && <OrderPagerReport />}
                    </>}

                    {!order.isLayout && ((order.mandate.path && order.mandate.reviewed) || !order.isGenerated) && <OrderPagerValidation />}
                    {order.isLayout && (order.mandate.path || !order.isGenerated) && <OrderPagerLayout />}
                </BreadcrumbList>
            </Breadcrumb>
        </div>
    </>
}

function OrderPagerItem({ to, label, labelAction, isDone, action, loading, disabled }) {
    const { page } = useParams()
    const { order } = useOrder()
    const url = `/orders/${order.pydio_id}`
    const navigate = useNavigate()


    if (action) {
        return <Button disabled={loading || disabled} onClick={action}>
            {loading && <LoaderCircleIcon className="h-4 w-4 animate-spin" />}
            {!loading && <ArrowRightIcon className="w-5 h-5" />}
            {labelAction}
        </Button>
    }

    return <>
        {to !== "mandate" && <BreadcrumbSeparator />}
        <BreadcrumbItem>
            <Button onClick={() => navigate(`${url}/${to}`)} variant={page === to ? "secondary" : "ghost"} className={clsx("text-md flex items-center", page === to && "text-foreground font-semibold border-primary border")}>
                {isDone ? <CheckCircle2Icon className="w-8 h-8 text-primary" /> : <CircleIcon className="w-8 h-8" />}
                <span className={clsx("", isDone && "text-primary")}>{label}</span>
            </Button>
        </BreadcrumbItem>
    </>
}

function OrderPagerLayout() {
    const { order, setOrder } = useOrder()
    const [loading, setLoading] = React.useState(false)
    const { session } = useAuth()
    const navigate = useNavigate()

    const finishLayout = async () => {
        if (!order.finished_at) {
            setLoading(true)

            const { error } = await fetch(`${APP_API_URL}/generation/mandate/${order.pydio_id}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify(order.mandate.form),
            }).then(res => res.json())

            setLoading(false)

            if (!error) {
                setOrder({
                    ...order,
                    mandate: {
                        ...order.mandate,
                        reviewed: true
                    }
                })
            } else {
                alert(error.message)
                return false;
            }
        }

        navigate(`/orders/${order.pydio_id}/validation`)
    }

    return <OrderPagerItem loading={loading} action={!order.mandate.reviewed && finishLayout} labelAction={"Créer le docx"} to="validation" label="Validation" isDone={order.finished_at} />
}

function OrderPagerReport() {
    const { order, setOrder } = useOrder()
    const [loading, setLoading] = React.useState(false)
    const { session } = useAuth()
    const navigate = useNavigate()


    const finishMandate = async () => {
        if (!order.finished_at) {
            setLoading(true)

            const { error } = await fetch(`${APP_API_URL}/generation/mandate/${order.pydio_id}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify(order.mandate.form),
            }).then(res => res.json())

            setLoading(false)

            if (!error) {
                setOrder({
                    ...order,
                    mandate: {
                        ...order.mandate,
                        reviewed: true
                    }
                })
            } else {
                alert(error.message)
                return false;
            }
        }

        navigate(`/orders/${order.pydio_id}/report`)
    }

    const { countSections, countSectionGenerated } = getCountSections(order)
    // const label = countSectionGenerated !== undefined ? `Transcription (${countSectionGenerated}/${countSections})` : "Transcription";
    const label = countSectionGenerated !== undefined ? `Transcription` : "Transcription";

    return <OrderPagerItem disabled={!order.sections.mandate_static} loading={loading} action={!order.mandate.reviewed && finishMandate} to="report" labelAction={"Générer la transcription"} label={label} isDone={order.is_sections_reviewed} />
}

function OrderPagerValidation() {
    const { order, setOrder } = useOrder()
    const [loading, setLoading] = React.useState(false)
    const { session } = useAuth()
    const navigate = useNavigate()

    const { countSections, countSectionGenerated } = getCountSections(order)

    const finishReport = async () => {
        if (!order.is_sections_reviewed || order.is_sections_dirty) {
            setLoading(true)

            const { error } = await fetch(`${APP_API_URL}/generation/report/${order.pydio_id}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                    sections_reviewed: order.sections,
                    sections_comment: order.sections_comment
                }),
            }).then(res => res.json())

            setLoading(false)

            if (!error) {
                setOrder({
                    ...order,
                    report_pspdfkit: null,
                    report_path: null,
                    is_sections_reviewed: true,
                    is_sections_dirty: false,
                })
            } else {
                alert(error.message)
                return false;
            }
        }

        navigate(`/orders/${order.pydio_id}/validation`)
    }

    return <OrderPagerItem disabled={!order.transcription || countSectionGenerated < countSections} loading={loading} action={(!order.is_sections_reviewed || order.is_sections_dirty) && finishReport} labelAction={"Créer le docx"} to="validation" label="Validation" isDone={order.finished_at} />
}


export function OrderLoader({ icon: Icon, text }) {
    return <div className="flex flex-grow justify-center items-center h-full">
        <div className="flex flex-col items-center gap-4 text-muted-foreground transform translate-y-[-50%]">
            <Icon className="h-16 w-16" strokeWidth={1} />

            <p className="text-sm font-semibold flex items-center justify-center text-lg select-none relative">
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin absolute" style={{ left: -24, top: 2 }} />
                {text}
            </p>
        </div>
    </div>
}

function getCountSections(order) {

    if (!order.transcription) {
        return {}
    }

    return {
        countSections: Object.keys(order.transcription).reduce((a, b) => {
            if (b !== "documentProvided" || !!order.transcription[b]?.text?.trim()?.length) {
                return a + 1
            }

            return a
        }, 0),
        countSectionGenerated: Object.keys(order.transcription).reduce((a, b) => {
            if (b === "antecedents" && order.sections["antecedents"]) {
                return a + 1
            }

            if (["currentComplaints", "historyOfCurrentDisease"].includes(b) && order.sections[b]) {
                return a + 1
            }

            if (cournoyer.find(section => section.transcription_key === b)?.value in order.sections) {
                return a + 1
            }

            return a
        }, 0)
    }
}